import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { apiClient } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { CallbackForm } from '@/components/partners/CallbackForm';
import { ShareButtons } from '@/components/partners/ShareButtons';
import { priceRange, firstImage } from '@/components/partners/utils';
import { extractDeveloperId } from '@/utils/slug';
import {
  MapPin, ArrowLeft, ShieldCheck, Building2, Globe, Layers,
  Home, CheckCircle, Hammer, KeyRound,
} from 'lucide-react';

const StatPill = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-2 rounded-xl border border-white/15">
    <Icon className="w-4 h-4 text-indigo-300" />
    <div className="leading-tight">
      <p className="text-sm font-semibold text-white">{value}</p>
      <p className="text-[11px] text-indigo-200/80">{label}</p>
    </div>
  </div>
);

const STATUS_LABEL = {
  under_construction: 'Under Construction',
  ready_to_move: 'Ready to Move',
  new_launch: 'New Launch',
  pre_launch: 'Pre Launch',
  nearing_possession: 'Nearing Possession',
};

const ProjectCard = ({ project }) => (
  <Link
    to={`/project/${project.project_id}`}
    className="group block bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
    data-testid={`developer-project-${project.project_id}`}
  >
    <div className="relative h-48 overflow-hidden bg-gray-100">
      <img
        src={firstImage(project.images)}
        alt={project.name}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
      {project.construction_status && (
        <span className="absolute top-3 left-3 text-[11px] font-semibold bg-indigo-600 text-white px-2.5 py-1 rounded-full shadow flex items-center gap-1">
          <Hammer className="w-3 h-3" /> {STATUS_LABEL[project.construction_status] || project.construction_status}
        </span>
      )}
    </div>
    <div className="p-4">
      <h3 className="font-semibold text-gray-900 truncate">{project.name}</h3>
      <p className="flex items-center gap-1 text-sm text-gray-500 mt-1">
        <MapPin className="w-3.5 h-3.5" /> {[project.locality, project.city].filter(Boolean).join(', ') || '—'}
      </p>
      <p className="text-indigo-700 font-bold mt-2">{priceRange(project.min_price, project.max_price)}</p>
      <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
        {project.total_units ? <span className="flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> {project.total_units} units</span> : null}
        {project.available_units ? <span className="flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> {project.available_units} available</span> : null}
        {project.possession_date ? <span className="flex items-center gap-1"><Home className="w-3.5 h-3.5" /> {project.possession_date}</span> : null}
      </div>
    </div>
  </Link>
);

export default function DeveloperProfile() {
  const { slugId } = useParams();
  const developerId = extractDeveloperId(slugId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiClient.get(`/public/developer/${developerId}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Developer not found');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [developerId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-5xl mx-auto px-4"><div className="h-96 bg-gray-200 rounded-2xl animate-pulse" /></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-gray-500 mb-4" data-testid="developer-not-found">{error || 'Developer not found'}</p>
          <Link to="/buy/properties"><Button variant="outline">Browse properties</Button></Link>
        </div>
      </div>
    );
  }

  const d = data.developer;
  const stats = d.stats || {};
  const displayName = d.company || d.name;
  const title = `${displayName} — Real Estate Developer`;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="developer-profile-page">
      <Helmet>
        <title>{title} | ZenStudios</title>
        <meta name="description" content={(d.description || `${displayName} is a verified real estate developer in ${d.city}. Explore ${data.projects_count} published projects and request a callback.`).slice(0, 160)} />
        <meta property="og:title" content={title} />
        <meta property="og:type" content="profile" />
      </Helmet>

      <section className="bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-900 text-white">
        <div className="max-w-5xl mx-auto px-4 pt-24 pb-12">
          <div className="flex items-center justify-between gap-3 mb-8">
            <Link to="/buy/properties" className="inline-flex items-center gap-1 text-indigo-300 hover:text-white text-sm transition-colors" data-testid="developer-back-link">
              <ArrowLeft className="w-4 h-4" /> Back to properties
            </Link>
            <ShareButtons title={displayName} testidPrefix="developer-share" />
          </div>
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="w-24 h-24 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center text-4xl font-bold overflow-hidden flex-shrink-0">
              {d.logo ? <img src={d.logo} alt={displayName} className="w-full h-full object-contain p-2" /> : displayName?.charAt(0)}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl font-bold" data-testid="developer-name">{displayName}</h1>
                {d.verified && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-indigo-200 bg-indigo-500/20 px-3 py-1 rounded-full" data-testid="developer-verified-badge">
                    <ShieldCheck className="w-3.5 h-3.5" /> Verified Developer
                  </span>
                )}
              </div>
              <p className="text-indigo-200/80 mt-1 flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {[d.city, d.state].filter(Boolean).join(', ')}</p>
              {d.website && (
                <a href={d.website} target="_blank" rel="noreferrer" className="text-indigo-300 hover:text-white text-sm mt-1 inline-flex items-center gap-1.5" data-testid="developer-website">
                  <Globe className="w-4 h-4" /> Visit website
                </a>
              )}
              <div className="flex flex-wrap gap-3 mt-5">
                <StatPill icon={Building2} label="Published projects" value={data.projects_count} />
                <StatPill icon={Layers} label="Total units" value={stats.total_units || 0} />
                <StatPill icon={CheckCircle} label="Projects delivered" value={stats.total_projects || 0} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {d.description && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="developer-about">
              <h2 className="font-semibold text-gray-900 mb-3">About {displayName}</h2>
              <p className="text-gray-600 leading-relaxed">{d.description}</p>
              {d.rera_number && (
                <p className="text-xs text-gray-400 mt-4 flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-indigo-500" /> RERA: {d.rera_number}
                </p>
              )}
            </div>
          )}

          <div data-testid="developer-projects-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900 text-lg">Projects</h2>
              <span className="text-sm text-gray-400">{data.projects_count} published</span>
            </div>
            {data.projects.length === 0 ? (
              <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center text-gray-400" data-testid="developer-no-projects">
                <Building2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
                No published projects yet. Request a callback to learn about upcoming launches.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-5">
                {data.projects.map((p) => <ProjectCard key={p.project_id} project={p} />)}
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-gray-100 p-6 lg:sticky lg:top-24" data-testid="developer-contact-card">
            <h3 className="font-semibold text-gray-900 mb-1">Enquire about projects</h3>
            <CallbackForm
              endpoint={`/public/developer/${developerId}/callback`}
              accent="indigo"
              testidPrefix="developer-callback"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
